import { useEffect, useRef, useState } from 'react';
import { Empty, Form, Radio, Spin, Table, Tag, message } from 'antd';
import Icon, { LoadingOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import Arrow from '@/assets/icons/chevron-right.svg?react';
import ArrowLeft from '@/assets/icons/chevron-left.svg?react';
import {
  useSearchAvailableNumbers,
  useProvisionNumber,
} from '@/hooks/use-phone-numbers/use-phone-numbers';
import {
  AvailableNumber,
  SearchAvailableParams,
} from '@/services/phone-numbers-service/phone-numbers-service.types';
import styles from './search-numbers.module.scss';
import { Button } from '../button/button';
import { Modal } from '../modal/modal';
import Dropdown from '../form/dropdown/dropdown';
import TextField from '../form/text-field/text-field';
import { useTranslation } from 'react-i18next';

interface SearchNumbersProps {
  companyId: number | undefined;
}

// Country codes the API accepts. The label is looked up at render time.
const COUNTRY_CODES = ['US', 'CA', 'GB'] as const;

const DEFAULT_FORM_STATE = {
  country_code: 'US',
  number_type: 'local',
  area_code: '',
  contains: '',
};

export function SearchNumbers({ companyId }: SearchNumbersProps) {
  const { t } = useTranslation(['phoneNumbers', 'common']);
  const [messageApi, contextHolder] = message.useMessage();
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [formState, setFormState] = useState(DEFAULT_FORM_STATE);
  const [confirmingNumber, setConfirmingNumber] = useState<string | null>(null);
  const prevCompanyId = useRef(companyId);

  useEffect(() => {
    if (prevCompanyId.current !== companyId) {
      setSearchTriggered(false);
      prevCompanyId.current = companyId;
    }
  }, [companyId]);

  const buildSearchParams = (): SearchAvailableParams => {
    const params: SearchAvailableParams = {
      company_id: companyId || 0,
      country_code: formState.country_code,
      number_type: formState.number_type,
    };
    if (formState.area_code) params.area_code = formState.area_code;
    if (formState.contains) params.contains = formState.contains;
    return params;
  };

  const { availableNumbers, isFetching } = useSearchAvailableNumbers(
    buildSearchParams(),
    searchTriggered && !!companyId
  );

  const { provision, isPending: isProvisioning } = useProvisionNumber(companyId);

  const handleSearch = () => {
    if (!companyId) return;
    setSearchTriggered(true);
  };

  const handleBuy = (phoneNumber: string) => {
    setConfirmingNumber(phoneNumber);
  };

  const handleConfirmBuy = () => {
    const numberToBuy = confirmingNumber;
    if (!numberToBuy) return;
    provision(
      { company_id: companyId!, phone_number: numberToBuy },
      {
        onSuccess: () => {
          messageApi.success(
            t('phoneNumbers:search.messages.provisioned', { phoneNumber: numberToBuy })
          );
          setConfirmingNumber(null);
          setSearchTriggered(false);
        },
        onError: () => {
          messageApi.error(t('phoneNumbers:search.messages.provisionFailed'));
          setConfirmingNumber(null);
        },
      }
    );
  };

  const columns: ColumnsType<AvailableNumber> = [
    {
      title: t('phoneNumbers:columns.phoneNumber'),
      dataIndex: 'phone_number',
      key: 'phone_number',
      className: styles.phoneNumberContainer,
    },
    {
      title: t('phoneNumbers:columns.friendlyName'),
      dataIndex: 'friendly_name',
      key: 'friendly_name',
      className: styles.friendlyNameContainer,
    },
    {
      title: t('phoneNumbers:columns.location'),
      key: 'location',
      className: styles.locationContainer,
      render: (_, record) =>
        [record.locality, record.region, record.iso_country].filter(Boolean).join(', '),
    },
    {
      title: t('phoneNumbers:columns.capabilities'),
      key: 'capabilities',
      className: styles.capabilitiesContainer,
      render: (_, record) => (
        <>
          {record.capabilities?.voice && (
            <Tag color="blue">{t('phoneNumbers:search.capabilities.voice')}</Tag>
          )}
          {record.capabilities?.SMS && (
            <Tag color="green">{t('phoneNumbers:search.capabilities.sms')}</Tag>
          )}
          {record.capabilities?.MMS && (
            <Tag color="orange">{t('phoneNumbers:search.capabilities.mms')}</Tag>
          )}
        </>
      ),
    },
    {
      title: t('phoneNumbers:columns.action'),
      key: 'action',
      className: styles.actionContainer,
      render: (_, record) => (
        <Button
          buttonVariant="secondary"
          size="small"
          onClick={() => handleBuy(record.phone_number)}
          disabled={isProvisioning}
        >
          {t('phoneNumbers:search.buy')}
        </Button>
      ),
    },
  ];

  return (
    <div>
      {contextHolder}
      <Form layout="inline" style={{ marginBottom: 24, flexWrap: 'wrap', gap: 8 }}>
        <Form.Item label={t('phoneNumbers:search.fields.country')} style={{ alignItems: 'center' }}>
          <Dropdown
            value={formState.country_code}
            onChange={(value) => {
              setFormState((prev) => ({ ...prev, country_code: value as string }));
              setSearchTriggered(false);
            }}
            options={COUNTRY_CODES.map((code) => ({
              label: t(`phoneNumbers:search.countries.${code}`),
              value: code,
            }))}
            allowClear={false}
            style={{ width: 180 }}
          />
        </Form.Item>

        <Form.Item label={t('phoneNumbers:search.fields.type')} style={{ alignItems: 'center' }}>
          <Radio.Group
            value={formState.number_type}
            onChange={(e) => {
              setFormState((prev) => ({ ...prev, number_type: e.target.value }));
              setSearchTriggered(false);
            }}
            className={styles.radioGroup}
          >
            <Radio.Button value="local">{t('phoneNumbers:search.numberTypes.local')}</Radio.Button>
            <Radio.Button value="toll_free">
              {t('phoneNumbers:search.numberTypes.toll_free')}
            </Radio.Button>
            <Radio.Button value="mobile">
              {t('phoneNumbers:search.numberTypes.mobile')}
            </Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label={t('phoneNumbers:search.fields.areaCode')}
          style={{ alignItems: 'center' }}
        >
          <TextField
            placeholder={t('phoneNumbers:search.placeholders.areaCode')}
            value={formState.area_code}
            onChange={(e) => {
              setFormState((prev) => ({ ...prev, area_code: e.target.value }));
              setSearchTriggered(false);
            }}
            style={{ width: 140 }}
          />
        </Form.Item>

        <Form.Item
          label={t('phoneNumbers:search.fields.contains')}
          style={{ alignItems: 'center' }}
        >
          <TextField
            placeholder={t('phoneNumbers:search.placeholders.contains')}
            value={formState.contains}
            onChange={(e) => {
              setFormState((prev) => ({ ...prev, contains: e.target.value }));
              setSearchTriggered(false);
            }}
            style={{ width: 140 }}
          />
        </Form.Item>

        <Form.Item>
          <Button buttonVariant="primary" onClick={handleSearch} disabled={!companyId} loading={isFetching}>
            {t('common:actions.search')}
          </Button>
        </Form.Item>
      </Form>

      <Modal
        isOpen={!!confirmingNumber}
        title={t('phoneNumbers:search.confirm.title')}
        onCancel={() => setConfirmingNumber(null)}
        onConfirm={handleConfirmBuy}
        isExporting={isProvisioning}
        confirmText={t('phoneNumbers:search.confirm.confirmButton')}
        cancelText={t('common:actions.cancel')}
      >
        <p>{t('phoneNumbers:search.confirm.text', { phoneNumber: confirmingNumber ?? '' })}</p>
      </Modal>

      <Table
        columns={columns}
        dataSource={availableNumbers}
        rowKey="phone_number"
        scroll={{ x: 800 }}
        className={styles.table}
        pagination={{
          position: ['bottomCenter'],
          pageSizeOptions: ['12', '25', '50'],
          showSizeChanger: true,
          defaultPageSize: 12,
          locale: { items_per_page: '' },
          nextIcon: <Icon component={() => <Arrow strokeWidth={1} width={16} height={16} />} />,
          prevIcon: <Icon component={() => <ArrowLeft strokeWidth={2} width={16} height={16} />} />,
        }}
        locale={{
          emptyText: isFetching ? (
            <div style={{ padding: '40px 0', textAlign: 'center' }}>
              <Spin indicator={<LoadingOutlined spin />} />
            </div>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                searchTriggered
                  ? t('phoneNumbers:search.empty.noResults')
                  : t('phoneNumbers:search.empty.prompt')
              }
            />
          ),
        }}
      />
    </div>
  );
}